import { useState } from "react";
import { Flex } from "antd";
import { colors } from "shared/theme/color";
import { translate } from "shared/languages";
import {
  VBOX_W,
  VBOX_H,
  PAD_LEFT,
  PAD_RIGHT,
  PAD_TOP,
  PLOT_H,
  niceMax,
  bandWidth,
  bandXFor,
  yFor,
} from "./chartUtils";
import { textPx } from "shared/constants/zoom";

interface SessionsCompletedChartProps {
  data: {
    month: string;
    month_label: string;
    open_sessions_completed: number;
    private_sessions_completed: number;
  }[];
}

const Legend = ({ color, label }: { color: string; label: string }) => (
  <Flex align="center" gap={6}>
    <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
    <span style={{ fontSize: textPx(12), color: colors.ink2 }}>{label}</span>
  </Flex>
);

const TIP_W = 140;
const TIP_H = 68;

// Stacked bars: Open (navy) + Private (the same pairing "Distribusi Sesi" /
// ClassTypeSplitChart uses), mirroring the Paket/Membership composition
// chart's shape so the two cards on this page read the same way — a
// month-by-month class-type trend that "Performa Kelas" doesn't show
// (that report only ever covers a single rolling window, not per month).
const SessionsCompletedChart = ({ data }: SessionsCompletedChartProps) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const isEmpty = data.every(
    (d) => d.open_sessions_completed === 0 && d.private_sessions_completed === 0,
  );
  const max = niceMax(
    Math.max(...data.map((d) => d.open_sessions_completed + d.private_sessions_completed), 1),
  );
  const baselineY = PAD_TOP + PLOT_H;
  const n = data.length;
  // Band scale, not xFor's point scale — a bar centered on an edge point
  // overflows the plot and covers the y-axis labels (see chartUtils).
  const band = bandWidth(n);
  const barWidth = Math.min(36, Math.max(10, band * 0.5));

  const bars = data.map((d, i) => {
    const x = bandXFor(i, n);
    const openTopY = yFor(d.open_sessions_completed, max);
    const totalTopY = yFor(d.open_sessions_completed + d.private_sessions_completed, max);
    return { ...d, x, openTopY, totalTopY };
  });

  const hovered = hoverIndex !== null ? bars[hoverIndex] : null;
  const openLabel = translate("analyticsPage.class.type_open");
  const privateLabel = translate("analyticsPage.class.type_private");
  const line1 = hovered ? `${openLabel}: ${hovered.open_sessions_completed}` : "";
  const line2 = hovered ? `${privateLabel}: ${hovered.private_sessions_completed}` : "";
  const tipLeft = hovered
    ? Math.min(Math.max(hovered.x - TIP_W / 2, PAD_LEFT), VBOX_W - PAD_RIGHT - TIP_W)
    : 0;
  const tipTop = hovered
    ? Math.max(Math.min(hovered.openTopY, hovered.totalTopY) - TIP_H - 10, PAD_TOP)
    : 0;

  return (
    <div>
      {/* Private = ink4, a deliberately visible warm grey: the near-white
          bgSoftHover fill was invisible against the card, while ink4 stays
          clearly readable yet lighter than navy so the stack boundary reads
          without a divider. */}
      <Flex gap={20} style={{ marginBottom: 16 }}>
        <Legend color={colors.navy} label={openLabel} />
        <Legend color={colors.ink4} label={privateLabel} />
      </Flex>

      {isEmpty ? (
        <Flex
          align="center"
          justify="center"
          style={{ height: VBOX_H, color: colors.ink3, fontSize: textPx(13) }}
        >
          {translate("analyticsPage.sales.trend_empty")}
        </Flex>
      ) : (
        <svg
          width="100%"
          height={VBOX_H}
          viewBox={`0 0 ${VBOX_W} ${VBOX_H}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {[0, 0.5, 1].map((f) => {
            const y = yFor(max * f, max);
            return (
              <g key={f}>
                <line
                  x1={PAD_LEFT}
                  x2={VBOX_W - 8}
                  y1={y}
                  y2={y}
                  stroke={colors.line}
                  strokeWidth={1}
                />
                <text x={PAD_LEFT - 8} y={y + 3} textAnchor="end" fontSize={10} fill={colors.ink3}>
                  {Math.round(max * f)}
                </text>
              </g>
            );
          })}

          {bars.map((b, i) => {
            const active = hoverIndex === i;
            return (
              <g key={b.month}>
                <rect
                  x={b.x - barWidth / 2}
                  y={b.openTopY}
                  width={barWidth}
                  height={Math.max(0, baselineY - b.openTopY)}
                  fill={colors.navy}
                  opacity={hoverIndex === null || active ? 1 : 0.45}
                />
                <rect
                  x={b.x - barWidth / 2}
                  y={b.totalTopY}
                  width={barWidth}
                  height={Math.max(0, b.openTopY - b.totalTopY)}
                  fill={colors.ink4}
                  opacity={hoverIndex === null || active ? 1 : 0.45}
                />
                <text x={b.x} y={VBOX_H - 8} textAnchor="middle" fontSize={10} fill={colors.ink3}>
                  {b.month}
                </text>
                {/* Full-band hit area, wider than the bar itself, so hovering
                    near a short bar doesn't require pixel-precise aim. */}
                <rect
                  x={b.x - band / 2}
                  y={PAD_TOP}
                  width={band}
                  height={PLOT_H}
                  fill="transparent"
                  onMouseEnter={() => setHoverIndex(i)}
                  onMouseLeave={() => setHoverIndex(null)}
                />
              </g>
            );
          })}

          {hovered && (
            <g pointerEvents="none">
              <rect
                x={tipLeft}
                y={tipTop}
                width={TIP_W}
                height={TIP_H}
                rx={6}
                fill={colors.ink}
              />
              <text x={tipLeft + 12} y={tipTop + 20} fontSize={12} fontWeight={600} fill={colors.card}>
                {hovered.month_label}
              </text>
              <circle cx={tipLeft + 15} cy={tipTop + 36} r={3.5} fill={colors.navy} />
              <text x={tipLeft + 24} y={tipTop + 40} fontSize={12} fill={colors.card}>
                {line1}
              </text>
              <circle cx={tipLeft + 15} cy={tipTop + 54} r={3.5} fill={colors.ink4} />
              <text x={tipLeft + 24} y={tipTop + 58} fontSize={12} fill={colors.card}>
                {line2}
              </text>
            </g>
          )}
        </svg>
      )}
    </div>
  );
};

export default SessionsCompletedChart;
